import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { ArrowUpRight, Calendar, Filter, Users, MousePointer, ShoppingCart, RefreshCcw } from 'lucide-react';
import './TrackingPage.scss';

const funnelData = [
    { name: 'Sent', value: 10000, fill: '#2784b9' },
    { name: 'Opened', value: 4500, fill: '#40a7d5' },
    { name: 'Clicked', value: 1200, fill: '#82c7e5' },
    { name: 'Converted', value: 480, fill: '#bce0f0' },
];

const engagementData = [
    { day: 'Mon', email: 400, whatsapp: 240 },
    { day: 'Tue', email: 300, whatsapp: 139 },
    { day: 'Wed', email: 200, whatsapp: 980 },
    { day: 'Thu', email: 278, whatsapp: 390 },
    { day: 'Fri', email: 189, whatsapp: 480 },
    { day: 'Sat', email: 239, whatsapp: 380 },
    { day: 'Sun', email: 349, whatsapp: 430 },
];

const COLORS = ['#2784b9', '#40a7d5', '#82c7e5', '#bce0f0'];

function TrackingPage() {
    return (
        <div className="tracking-root">
            <div className="tracking-header-row">
                <div>
                    <h1 className="tracking-title">Campaign Tracking</h1>
                    <p className="tracking-desc">In-depth analysis of your marketing efforts.</p>
                </div>
                <div className="tracking-header-actions">
                    <button className="tracking-btn"><Calendar size={18} /><span>Last 30 Days</span></button>
                    <button className="tracking-btn"><Filter size={18} /><span>Filter</span></button>
                </div>
            </div>
            <div className="tracking-main-grid">
                <div className="tracking-main-content">
                    <div className="tracking-card">
                        <div className="tracking-card-header">
                            <h3>Engagement Multi-Channel</h3>
                            <div className="tracking-legend">
                                <div className="tracking-legend-item"><div className="tracking-legend-dot email"></div> Email</div>
                                <div className="tracking-legend-item"><div className="tracking-legend-dot whatsapp"></div> WhatsApp</div>
                            </div>
                        </div>
                        <div className="tracking-chart-area">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={engagementData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 12 }} />
                                    <Tooltip contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                                    <Line type="monotone" dataKey="email" stroke="#2784b9" strokeWidth={3} dot={{ fill: '#2784b9', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
                                    <Line type="monotone" dataKey="whatsapp" stroke="#10b981" strokeWidth={3} dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }} activeDot={{ r: 6 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div className="tracking-card-grid">
                        <div className="tracking-card">
                            <h3>Conversion Funnel</h3>
                            <div className="tracking-chart-bar">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={funnelData} layout="vertical" margin={{ left: -10 }}>
                                        <XAxis type="number" hide />
                                        <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                                        <Tooltip />
                                        <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={24}>
                                            {funnelData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="tracking-card">
                            <h3>ROI by Channel</h3>
                            <div className="tracking-chart-pie">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie data={funnelData} cx="50%" cy="50%" innerRadius={60} outerRadius={80} paddingAngle={5} dataKey="value">
                                            {funnelData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="tracking-pie-legend">
                                {funnelData.map((item, idx) => (
                                    <div key={item.name} className="tracking-pie-legend-item">
                                        <div className="tracking-pie-legend-dot" style={{ backgroundColor: COLORS[idx] }}></div>
                                        {item.name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="tracking-side-content">
                    <div className="tracking-card">
                        <h3>Real-time Performance</h3>
                        <div className="tracking-performance-list">
                            <div className="tracking-performance-item">
                                <div className="tracking-performance-icon users"><Users size={18} /></div>
                                <div>
                                    <div className="tracking-performance-value">4,284</div>
                                    <div className="tracking-performance-label">Impressions</div>
                                </div>
                                <div className="tracking-performance-change positive"><ArrowUpRight size={14} /> +4.2%</div>
                            </div>
                            <div className="tracking-performance-item">
                                <div className="tracking-performance-icon mouse"><MousePointer size={18} /></div>
                                <div>
                                    <div className="tracking-performance-value">842</div>
                                    <div className="tracking-performance-label">Clicks</div>
                                </div>
                                <div className="tracking-performance-change positive"><ArrowUpRight size={14} /> +2.1%</div>
                            </div>
                            <div className="tracking-performance-item">
                                <div className="tracking-performance-icon cart"><ShoppingCart size={18} /></div>
                                <div>
                                    <div className="tracking-performance-value">124</div>
                                    <div className="tracking-performance-label">Sales</div>
                                </div>
                                <div className="tracking-performance-change negative"><ArrowUpRight size={14} className="rotate-90" /> -0.8%</div>
                            </div>
                            <div className="tracking-refresh">
                                <RefreshCcw size={16} /> Refresh Data
                            </div>
                        </div>
                    </div>
                    <div className="tracking-tip-card">
                        <h3>Tracking Tip</h3>
                        <p>Campaigns sent on Tuesday between 10 AM and 11 AM have seen a 14% higher open rate this month. Consider scheduling your next blast accordingly.</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TrackingPage;
